import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import LegalFooter from "@/components/LegalFooter";
import { Button } from "@/components/ui/button";
import { Sparkles, RefreshCw, TrendingUp, AlertTriangle, ClipboardList, Loader2, ExternalLink, Info } from "lucide-react";
import { api, login } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

export default function Dashboard() {
  const { user, loading: authLoading, refresh } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      login();
      return;
    }
    Promise.all([api.get("/dashboard/summary"), api.get("/audits")])
      .then(([s, a]) => {
        setSummary(s.data);
        setAudits(a.data);
      })
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#080A11] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
      </div>
    );
  }

  const chartData = (summary?.score_evolution || []).map((e) => ({
    date: new Date(e.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }),
    score: e.score,
  }));

  return (
    <div className="min-h-screen bg-[#080A11] text-slate-100">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 pt-28 pb-24" data-testid="dashboard-container">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Bonjour {user?.name?.split(" ")[0]} 👋</h1>
            <p className="text-slate-400 mt-1">Voici l'état de vos audits.</p>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <span
                data-testid="dashboard-plan-badge"
                className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                  user?.plan === "pro"
                    ? "bg-violet-500/20 text-violet-300 border-violet-500/30"
                    : user?.plan === "agency"
                    ? "bg-blue-500/20 text-blue-300 border-blue-500/30"
                    : "bg-slate-700/40 text-slate-300 border-slate-600/40"
                }`}
              >
                Plan {user?.plan?.toUpperCase() || "FREE"}
              </span>
              {user?.plan === "free" && (
                <span className="text-xs text-slate-500" data-testid="dashboard-quota-info">
                  {user?.audits_used_this_period ?? 0}/1 audit ce mois-ci
                </span>
              )}
              {(user?.plan === "pro" || user?.plan === "agency") && (
                <span className="text-xs text-slate-500" data-testid="dashboard-quota-info">
                  {(user?.audits_used_this_period ?? 0)}/{user?.audits_quota ?? "—"} audits utilisés
                  {user?.quota_renewal_date && (
                    <> · renouvellement le {new Date(user.quota_renewal_date).toLocaleDateString("fr-FR")}</>
                  )}
                </span>
              )}
              {(user?.plan === "pro" || user?.plan === "agency") && user?.stripe_subscription_id && (
                <Button
                  data-testid="dashboard-manage-sub-btn"
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/subscription/portal", {
                        return_url: window.location.origin,
                      });
                      window.location.href = data.portal_url;
                    } catch (e) {
                      const detail = e?.response?.data?.detail;
                      const msg =
                        typeof detail === "string"
                          ? detail
                          : detail?.message || "Impossible d'ouvrir le portail Stripe.";
                      toast.error(msg);
                    }
                  }}
                  className="text-xs text-slate-300 hover:text-violet-300 hover:bg-violet-500/10 h-7"
                >
                  Gérer mon abonnement
                </Button>
              )}
            </div>
          </div>
          <Button
            data-testid="dashboard-new-audit-btn"
            onClick={() => navigate("/analyze")}
            disabled={user?.can_run_audit === false}
            className="bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 border-0 h-11 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Nouvel audit
          </Button>
        </div>

        {/* Quota alerts — Pro & Agency only. Free's block is handled by the CTA + Analyze page. */}
        {(user?.plan === "pro" || user?.plan === "agency") && user?.quota_status === "blocked" && (
          <div
            data-testid="quota-alert-blocked"
            className="mb-6 rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
            <div className="text-sm text-rose-100">
              <div className="font-semibold text-rose-200">Quota atteint ({user.audits_used_this_period}/{user.audits_quota} audits sur 30 jours).</div>
              <div className="mt-1 text-rose-100/80">
                La création de nouveaux audits est bloquée jusqu'au renouvellement
                {user.quota_renewal_date && <> le {new Date(user.quota_renewal_date).toLocaleDateString("fr-FR")}</>}.
                Vos rapports existants restent accessibles.
                {user.plan === "pro" && (
                  <> Besoin de plus ?{" "}
                    <a href="/pricing" className="underline font-semibold">Passer à Agency (1 000 audits / 30 jours)</a>.
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {(user?.plan === "pro" || user?.plan === "agency") && user?.quota_status === "warn" && (
          <div
            data-testid="quota-alert-warn"
            className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-100">
              <span className="font-semibold text-amber-200">Vous approchez de votre limite mensuelle.</span>{" "}
              {user.audits_used_this_period}/{user.audits_quota} audits utilisés sur les 30 derniers jours.
              {user.plan === "pro" && <> Envisagez le forfait <a href="/pricing" className="underline">Agency</a> pour plus de volume.</>}
            </div>
          </div>
        )}
        {(user?.plan === "pro" || user?.plan === "agency") && user?.quota_status === "info" && (
          <div
            data-testid="quota-alert-info"
            className="mb-6 rounded-xl border border-slate-700 bg-slate-800/40 p-4 flex items-start gap-3"
          >
            <Info className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />
            <div className="text-sm text-slate-200">
              {user.audits_used_this_period}/{user.audits_quota} audits utilisés sur les 30 derniers jours.
              {user.quota_renewal_date && <> Renouvellement le {new Date(user.quota_renewal_date).toLocaleDateString("fr-FR")}.</>}
            </div>
          </div>
        )}

        {summary?.audits_count === 0 ? (
          <div className="glass rounded-3xl p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-7 h-7 text-violet-400" />
            </div>
            <h2 className="font-display text-2xl font-bold text-white mb-2">Lancez votre premier audit</h2>
            <p className="text-slate-400 mb-6">Entrez l'URL de votre boutique pour découvrir votre Revenue Score.</p>
            <Button
              onClick={() => navigate("/analyze")}
              className="bg-gradient-to-r from-violet-600 to-blue-600 border-0 h-11 px-6"
            >
              Commencer l'audit
            </Button>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="glass rounded-2xl p-6" data-testid="dashboard-stat-score">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Score actuel</div>
                <div className="font-display text-4xl font-bold text-white">{summary.current_score}<span className="text-lg text-slate-500 ml-1">/100</span></div>
              </div>
              <div className="glass rounded-2xl p-6" data-testid="dashboard-stat-audits">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Audits réalisés</div>
                <div className="font-display text-4xl font-bold text-white">{summary.audits_count}</div>
              </div>
              <div className="glass rounded-2xl p-6" data-testid="dashboard-stat-critical">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Problèmes critiques</div>
                <div className="font-display text-4xl font-bold text-rose-400">{summary.critical_count}</div>
              </div>
            </div>

            {/* Chart */}
            <div className="glass rounded-2xl p-8 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-display text-xl font-semibold text-white">Évolution du score</h2>
                  <p className="text-xs text-slate-500 mt-1">Vos {chartData.length} derniers audits</p>
                </div>
                <TrendingUp className="w-5 h-5 text-violet-400" />
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" stroke="#475569" fontSize={11} />
                    <YAxis stroke="#475569" fontSize={11} domain={[0, 100]} />
                    <Tooltip
                      contentStyle={{ background: "#0F1322", border: "1px solid #1e293b", borderRadius: 12, color: "#fff" }}
                      cursor={{ stroke: "#8B5CF6", strokeWidth: 1 }}
                    />
                    <Area type="monotone" dataKey="score" stroke="#8B5CF6" strokeWidth={2} fill="url(#scoreGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* History */}
            <div className="glass rounded-2xl p-8" data-testid="dashboard-history-table">
              <h2 className="font-display text-xl font-semibold text-white mb-6">Historique des audits</h2>
              <div className="space-y-2">
                {audits.map((a) => (
                  <div
                    key={a.audit_id}
                    data-testid={`history-row-${a.audit_id}`}
                    onClick={() => navigate(`/results/${a.audit_id}`)}
                    className="flex items-center justify-between p-4 rounded-xl hover:bg-slate-800/40 cursor-pointer transition-colors group border border-transparent hover:border-slate-800"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-display font-bold text-lg ${
                        a.revenue_score >= 85 ? "bg-emerald-500/10 text-emerald-400" :
                        a.revenue_score >= 70 ? "bg-violet-500/10 text-violet-400" :
                        a.revenue_score >= 50 ? "bg-amber-500/10 text-amber-400" :
                        "bg-rose-500/10 text-rose-400"
                      }`}>
                        {a.revenue_score}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-white truncate">{a.url.replace(/^https?:\/\//, "")}</div>
                        <div className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString("fr-FR")}</div>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-violet-400 shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      <LegalFooter />
    </div>
  );
}
